// Weekly digest for Parent Home (Alan, 15 Sep): "Last week in Learn & Earn".
//
// The engine's weeklySummaryFor(child, learning, todayStr) counts the last seven days from
// TODAY — a rolling window, recomputed on every load — and the card that showed it was titled
// "This week". Alan's mental model, and the Monday push's, is a digest: Monday-to-Sunday just
// gone, appearing on Monday, the same numbers every time it is opened until next Monday
// replaces it. The engine function cannot be given a bounded past week (it has a start and no
// end), and the engine file is not mine to change, so this module builds the same sentence
// shape over an explicit [weekStart, weekEnd] and the card reads it instead.
//
// Pure reads over engine state; nothing here writes.
import { dateStrAddDays, weekStartOf } from "./utils.js";

export const DIGEST_GAME_NAMES = { spelling: "spelling", vocab: "vocabulary", mathsworkout: "maths", ttstreaks: "times tables" };

// The previous Monday..Sunday for a given today, as date strings (inclusive).
export function lastWeekBounds(todayStr) {
  const thisMonday = weekStartOf(todayStr);
  return { start: dateStrAddDays(thisMonday, -7), end: dateStrAddDays(thisMonday, -1) };
}

// Same shape and wording as the engine's weeklySummaryFor, over a bounded week.
export function digestFor(child, learning, { start, end }, gameNames = DIGEST_GAME_NAMES) {
  const from = Date.parse(start + "T00:00:00"), to = Date.parse(end + "T23:59:59.999");
  const inWeek = (day) => { const t = Date.parse(day); return t >= from && t <= to; };
  const adaptive = (learning && learning.adaptive) || {};
  const parts = []; let any = false; let mastered = 0; let focus = null;
  for (const [game, st] of Object.entries(adaptive)) {
    if (!st || typeof st !== "object" || !st.sessions) continue;
    const played = (st.sessions || []).filter((x) => x.day && inWeek(x.day)).length;
    if (played) { any = true; parts.push(`${played} ${gameNames[game] || game} test${played === 1 ? "" : "s"}`); }
    mastered += Object.values(st.topics || {}).filter((t) => t.mastered && t.masteredAt && inWeek(t.masteredAt.slice(0, 10))).length;
    // "Focus area" is the CURRENT practice queue — what is coming back — not a week-bounded
    // count, exactly as the engine treats it.
    if (!focus && Array.isArray(st.practice) && st.practice.length >= 3) focus = { game, count: st.practice.length };
  }
  if (!any) return null;
  const did = parts.join(", ");
  return {
    name: child.name, did, mastered, focus,
    text: `${child.name} did ${did}${mastered ? ` and mastered ${mastered} topic${mastered === 1 ? "" : "s"}` : ""}.${focus ? ` Focus area: ${gameNames[focus.game] || focus.game}, ${focus.count} tricky ones coming back.` : ""}`,
  };
}

// "Mon 8 – Sun 14 September", or "Mon 29 September – Sun 5 October" across a month boundary.
export function digestRangeLabel({ start, end }) {
  const d = (s) => new Date(s + "T00:00:00");
  const day = (x) => x.toLocaleDateString("en-GB", { weekday: "short" });
  const num = (x) => x.getDate();
  const mon = (x) => x.toLocaleDateString("en-GB", { month: "long" });
  const a = d(start), b = d(end);
  return mon(a) === mon(b)
    ? `${day(a)} ${num(a)} – ${day(b)} ${num(b)} ${mon(b)}`
    : `${day(a)} ${num(a)} ${mon(a)} – ${day(b)} ${num(b)} ${mon(b)}`;
}
